import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import LeagueSwitcher from "@/components/LeagueSwitcher";
import { useI18n } from "@/i18n/I18nContext";

export default function Teams() {
  const { t } = useI18n();
  const [league, setLeague] = useState("all");
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (league !== "all") params.league = league;
    api.get("/teams", { params })
      .then((r) => setTeams(r.data.teams || []))
      .finally(() => setLoading(false));
  }, [league]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter((tm) =>
      [(tm.name || ""), (tm.short || ""), (tm.league_name || ""), (tm.country || "")]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [teams, query]);

  // Group by league for display
  const grouped = useMemo(() => {
    const out = new Map();
    for (const tm of filtered) {
      const key = tm.league_name || "—";
      if (!out.has(key)) out.set(key, []);
      out.get(key).push(tm);
    }
    return [...out.entries()];
  }, [filtered]);

  return (
    <div data-testid="teams-page" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="border-b border-white/10 pb-6">
        <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-tighter">{t("teams.title")}</h1>
        <p className="text-sm text-white/50 mt-2">
          {loading ? t("matches.loading") : t("teams.subtitle", { n: filtered.length })}
        </p>
      </div>

      <div className="mt-6 space-y-4">
        <div className="flex items-center gap-3 border border-white/15 px-4 py-2.5 focus-within:border-volt transition-colors">
          <Search className="w-4 h-4 text-white/40 shrink-0" />
          <input
            data-testid="teams-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("teams.search_placeholder")}
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/30"
          />
        </div>
        <LeagueSwitcher value={league} onChange={setLeague} testIdPrefix="teams-league" />
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="text-center text-white/40 py-20 text-sm uppercase tracking-[0.2em]">
            {t("matches.loading")}
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="teams-empty" className="text-center text-white/40 py-20 text-sm uppercase tracking-[0.2em]">
            {t("teams.empty")}
          </div>
        ) : (
          <div className="space-y-10" data-testid="teams-grouped">
            {grouped.map(([leagueName, list]) => (
              <section key={leagueName}>
                <div className="flex items-end justify-between mb-3 border-b border-white/5 pb-2">
                  <h2 className="font-display text-2xl uppercase tracking-tighter">{leagueName}</h2>
                  <span className="text-xs uppercase tracking-[0.2em] text-white/40 font-mono">{list.length}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {list.map((tm) => (
                    <div
                      key={tm.id}
                      data-testid={`team-card-${tm.id}`}
                      className="border border-white/10 bg-[#121212] p-3 card-lift flex flex-col items-center text-center gap-2"
                    >
                      <div className="w-12 h-12 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
                        {tm.logo_url ? (
                          <img src={tm.logo_url} alt={tm.short} className="w-9 h-9 object-contain" />
                        ) : (
                          <span className="font-mono text-[10px]">{tm.logo}</span>
                        )}
                      </div>
                      <div className="text-xs font-medium leading-tight truncate w-full">{tm.name}</div>
                      <div className="text-[9px] uppercase tracking-[0.18em] text-white/40">
                        {tm.rank ? `#${tm.rank}` : ""} {tm.points != null ? `• ${tm.points} pts` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
